/**
 * Form Completion Cache Utility
 * Implements a simple Map-based cache with TTL (Time To Live) for form completion status
 *
 * Performance targets:
 * - Cache hit should return in <5ms
 * - 80% cache hit rate expected
 * - Reduces API calls by ~80%
 *
 * Cache TTL: 1 minute (60000ms)
 */

import { FormCompletionStatus } from '../types/onboarding';
import { logger } from 'utils/logger';

/**
 * Cache entry structure with data, timestamp, and TTL
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Cache configuration constants
 */
const CACHE_CONFIG = {
  DEFAULT_TTL: 60000, // 1 minute in milliseconds
} as const;

/**
 * FormCompletionCache class
 * Manages in-memory cache for form completion status with automatic expiration
 */
class FormCompletionCache {
  private cache: Map<string, CacheEntry<FormCompletionStatus>>;

  constructor() {
    this.cache = new Map();
  }

  /**
   * Generate cache key from userId and eventId
   * @param userId - User UUID
   * @param eventId - Event ID
   * @returns Cache key string
   */
  private generateKey(userId: string, eventId: string): string {
    return `${userId}:${eventId}`;
  }

  /**
   * Check if a cache entry is expired
   * @param entry - Cache entry to check
   * @returns true if expired, false otherwise
   */
  private isExpired(entry: CacheEntry<FormCompletionStatus>): boolean {
    const now = Date.now();
    const age = now - entry.timestamp;
    return age > entry.ttl;
  }

  /**
   * Get cached form completion status
   * @param userId - User UUID
   * @param eventId - Event ID
   * @returns Cached FormCompletionStatus or null if not found/expired
   */
  get(userId: string, eventId: string): FormCompletionStatus | null {
    const key = this.generateKey(userId, eventId);
    const entry = this.cache.get(key);

    if (!entry) {
      logger.debug('Cache miss for:', { key });
      return null;
    }

    if (this.isExpired(entry)) {
      logger.debug('Cache expired for:', { key });
      this.cache.delete(key);
      return null;
    }

    logger.debug('Cache hit for:', { key });
    return entry.data;
  }

  /**
   * Set form completion status in cache
   * @param userId - User UUID
   * @param eventId - Event ID
   * @param data - Form completion status to cache
   * @param ttl - Time to live in milliseconds (default: 1 minute)
   */
  set(
    userId: string,
    eventId: string,
    data: FormCompletionStatus,
    ttl: number = CACHE_CONFIG.DEFAULT_TTL
  ): void {
    const key = this.generateKey(userId, eventId);
    const entry: CacheEntry<FormCompletionStatus> = {
      data,
      timestamp: Date.now(),
      ttl,
    };

    this.cache.set(key, entry);
    logger.debug('Cached data for:', { key, ttl_ms: ttl });
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    logger.debug('Cleared cache, removed entries:', { size });
  }

  /**
   * Clear cache entry for specific user and event
   * @param userId - User UUID
   * @param eventId - Event ID
   */
  clearEntry(userId: string, eventId: string): void {
    const key = this.generateKey(userId, eventId);
    const deleted = this.cache.delete(key);
    if (deleted) {
      logger.debug('Cleared cache entry for:', { key });
    }
  }

  /**
   * Get current cache size
   * @returns Number of entries in cache
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Clean up expired entries
   * Useful for preventing memory leaks in long-running applications
   */
  cleanupExpired(): number {
    let cleaned = 0;
    const now = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      logger.debug('Cleaned up expired entries:', { cleaned });
    }

    return cleaned;
  }
}

/**
 * Singleton instance of FormCompletionCache
 * Export as singleton to ensure cache persistence across components
 */
export const formCompletionCache = new FormCompletionCache();

/**
 * Export class for testing purposes
 */
export { FormCompletionCache };

/**
 * Export cache configuration constants
 */
export { CACHE_CONFIG };
